/**
 * Gerador de PDF profissional para Relatório Financeiro AgroLink
 *
 * Estrutura do PDF:
 * 1. Cabeçalho: logo empresa | nome empresa | título Relatório
 * 2. Info box: período, gerado por, gerado em
 * 3. Card com filtros aplicados
 * 4. Grid 5 colunas de totalizadores
 * 5. Tabela: Resumo por Responsável
 * 6. Tabela principal: Serviços
 * 7. Detalhamento (se detalhado=true): subserviços e despesas por serviço
 * 8. Rodapé: paginação, sistema, data/hora
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const fmtMoney = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d + (d.length === 10 ? 'T12:00:00' : '')).toLocaleDateString('pt-BR') : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('pt-BR') : '—';

const STATUS_LABEL = {
  em_andamento: 'Em Andamento',
  ativo: 'Em Andamento',
  pausado: 'Pausado',
  aguardando_cliente: 'Aguardando Cliente',
  aguardando_pagamento: 'Aguardando Pagamento',
  concluido: 'Concluído',
  cancelado: 'Cancelado',
};

// Paleta de cores (AgroLink identity — verde primary)
const COR_PRIMARIA = [21, 128, 61];    // green-700 (igual botões)
const COR_ESCURA = [15, 23, 42];       // slate-900
const COR_MEDIA = [71, 85, 105];       // slate-600
const COR_CLARA = [148, 163, 184];     // slate-400
const COR_BORDA = [226, 232, 240];     // slate-200
const COR_HIGHLIGHT = [240, 253, 244]; // green-50
const COR_VERDE = [16, 185, 129];      // emerald-500
const COR_VERMELHO = [239, 68, 68];    // red-500
const COR_AMBAR = [245, 158, 11];      // amber-500

/**
 * Gera o PDF do relatório financeiro
 */
export async function gerarRelatorioPDF({ data, detalhado, empresa, filtrosLabels }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 12;

  // =================================================================
  // 1) CABEÇALHO DA PRIMEIRA PÁGINA
  // =================================================================
  let cursorY = margin;

  // Faixa de topo azul cyan
  doc.setFillColor(...COR_PRIMARIA);
  doc.rect(0, 0, pageWidth, 30, 'F');

  // Logo da empresa (se houver base64)
  const logoBase64 = empresa?.logo_base64;
  const nomeEmpresa = empresa?.nome_empresa || 'AgroLink CRM';
  let logoOffset = margin;

  if (logoBase64) {
    try {
      // Detecta se já vem com prefixo data:image
      const src = logoBase64.startsWith('data:') ? logoBase64 : `data:image/png;base64,${logoBase64}`;
      doc.addImage(src, 'PNG', margin, 6, 18, 18);
      logoOffset = margin + 22;
    } catch (e) {
      console.warn('Falha ao adicionar logo no PDF:', e);
    }
  }

  // Nome da empresa
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(nomeEmpresa.toUpperCase(), logoOffset, 14);

  // Subtítulo à esquerda
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(220, 252, 231); // verde-100 no header
  doc.text('Sistema de Gestão AgroLink', logoOffset, 20);

  // Título "RELATÓRIO FINANCEIRO" à direita
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('RELATÓRIO FINANCEIRO', pageWidth - margin, 14, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Serviços · Despesas · Resultado', pageWidth - margin, 20, { align: 'right' });

  cursorY = 38;

  // =================================================================
  // 2) BLOCO DE INFO: emitido em, emitido por
  // =================================================================
  doc.setTextColor(...COR_MEDIA);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  const infoBoxY = cursorY;
  doc.setFillColor(...COR_HIGHLIGHT);
  doc.setDrawColor(...COR_BORDA);
  doc.roundedRect(margin, infoBoxY, pageWidth - margin * 2, 14, 1.5, 1.5, 'FD');

  const colWidth = (pageWidth - margin * 2) / 3;

  // Coluna 1: Emitido em
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...COR_CLARA);
  doc.text('EMITIDO EM', margin + 4, infoBoxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COR_ESCURA);
  doc.text(fmtDateTime(data.gerado_em), margin + 4, infoBoxY + 10);

  // Coluna 2: Emitido por
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...COR_CLARA);
  doc.text('EMITIDO POR', margin + 4 + colWidth, infoBoxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COR_ESCURA);
  doc.text(data.gerado_por || '—', margin + 4 + colWidth, infoBoxY + 10);

  // Coluna 3: Quantidade / Total
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(...COR_CLARA);
  doc.text('SERVIÇOS NO RELATÓRIO', margin + 4 + colWidth * 2, infoBoxY + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...COR_ESCURA);
  doc.text(`${data.totais.qtde_servicos} serviço(s) · ${fmtMoney(data.totais.valor_total_servicos)}`, margin + 4 + colWidth * 2, infoBoxY + 10);

  cursorY = infoBoxY + 18;

  // =================================================================
  // 3) FILTROS APLICADOS
  // =================================================================
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(...COR_BORDA);
  const filtrosH = Math.max(14, 6 + Math.ceil(filtrosLabels.length / 3) * 8);
  doc.roundedRect(margin, cursorY, pageWidth - margin * 2, filtrosH, 1.5, 1.5, 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...COR_PRIMARIA);
  doc.text('FILTROS APLICADOS', margin + 4, cursorY + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  filtrosLabels.forEach((f, i) => {
    const col = i % 3;
    const row = Math.floor(i / 3);
    const x = margin + 4 + col * ((pageWidth - margin * 2) / 3);
    const y = cursorY + 10 + row * 5;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COR_MEDIA);
    doc.text(`${f.label}:`, x, y);
    const labelWidth = doc.getTextWidth(`${f.label}: `);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COR_ESCURA);
    const maxWidth = ((pageWidth - margin * 2) / 3) - labelWidth - 4;
    const truncatedValue = String(f.value).length > 40 ? String(f.value).substring(0, 40) + '...' : String(f.value);
    doc.text(truncatedValue, x + labelWidth, y, { maxWidth });
  });

  cursorY += filtrosH + 4;

  // =================================================================
  // 4) TOTALIZADORES — grid 5 colunas
  // =================================================================
  const totais = [
    { label: 'SERVIÇOS', value: String(data.totais.qtde_servicos), hint: fmtMoney(data.totais.valor_total_servicos), cor: COR_PRIMARIA },
    { label: 'RECEBIDO', value: fmtMoney(data.totais.valor_recebido), hint: null, cor: COR_VERDE },
    { label: 'A RECEBER', value: fmtMoney(data.totais.valor_pendente), hint: null, cor: COR_AMBAR },
    { label: 'DESPESAS', value: fmtMoney(data.totais.valor_despesas), hint: null, cor: COR_VERMELHO },
    { label: 'LUCRO', value: fmtMoney(data.totais.lucro_estimado), hint: '(Recebido − Despesas)', cor: data.totais.lucro_estimado >= 0 ? COR_VERDE : COR_VERMELHO },
  ];

  const cardW = (pageWidth - margin * 2 - 4 * 2) / 5;
  const cardH = 18;
  totais.forEach((t, i) => {
    const x = margin + i * (cardW + 2);
    // Fundo levemente colorido
    doc.setFillColor(252, 252, 251);
    doc.setDrawColor(...t.cor);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, cursorY, cardW, cardH, 1.5, 1.5, 'FD');

    // Barra colorida na esquerda
    doc.setFillColor(...t.cor);
    doc.rect(x, cursorY, 1.5, cardH, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...t.cor);
    doc.text(t.label, x + 4, cursorY + 5);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COR_ESCURA);
    doc.text(t.value, x + 4, cursorY + 11);

    if (t.hint) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.5);
      doc.setTextColor(...COR_CLARA);
      doc.text(t.hint, x + 4, cursorY + 15);
    }
  });

  cursorY += cardH + 6;

  // =================================================================
  // 5) TABELA: Resumo por Responsável
  // =================================================================
  if (data.por_responsavel && data.por_responsavel.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COR_PRIMARIA);
    doc.text('RESUMO POR RESPONSÁVEL', margin, cursorY);
    cursorY += 2;

    autoTable(doc, {
      startY: cursorY,
      head: [['Responsável', 'Qtde', 'Valor Total']],
      body: data.por_responsavel.map(r => [
        r.nome,
        String(r.qtde),
        fmtMoney(r.valor),
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineColor: COR_BORDA,
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: COR_PRIMARIA,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'right', cellWidth: 25 },
        2: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
      },
      margin: { left: margin, right: margin },
    });

    cursorY = doc.lastAutoTable.finalY + 6;
  }

  // =================================================================
  // 6) TABELA PRINCIPAL: Serviços
  // =================================================================
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...COR_PRIMARIA);
  doc.text('SERVIÇOS', margin, cursorY);
  cursorY += 2;

  const servicosBody = [];
  data.servicos.forEach((s) => {
    servicosBody.push([
      s.cliente_nome || '—',
      s.titulo + (s.tipo_servico_nome ? `\n${s.tipo_servico_nome}` : ''),
      s.criado_por_nome || '—',
      STATUS_LABEL[s.status] || s.status || '—',
      fmtDate(s.data_inicio),
      fmtMoney(s.valor_total),
      fmtMoney(s.valor_pago),
      fmtMoney(s.total_despesas),
    ]);

    // Se detalhado, adiciona linhas com subserviços e despesas
    if (detalhado) {
      if ((s.itens || []).length > 0) {
        const itensText = s.itens.map(it => `» ${it.descricao || 'sem descrição'}: ${fmtMoney(it.valor)}`).join('\n');
        servicosBody.push([
          { content: `Subserviços:\n${itensText}`, colSpan: 8, styles: { fillColor: [248, 250, 252], textColor: COR_MEDIA, fontSize: 7, cellPadding: { top: 2, right: 4, bottom: 2, left: 8 } } }
        ]);
      }
      if ((s.despesas_vinculadas || []).length > 0) {
        const despText = s.despesas_vinculadas.map(d =>
          `» ${fmtDate(d.data)} · ${d.categoria_nome || 'sem categoria'}${d.observacoes ? ` · ${d.observacoes}` : ''}: ${fmtMoney(d.valor)}`
        ).join('\n');
        servicosBody.push([
          { content: `Despesas vinculadas:\n${despText}`, colSpan: 8, styles: { fillColor: [254, 242, 242], textColor: [153, 27, 27], fontSize: 7, cellPadding: { top: 2, right: 4, bottom: 2, left: 8 } } }
        ]);
      }
    }
  });

  // Linha total no rodapé
  servicosBody.push([
    { content: 'TOTAIS', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: COR_ESCURA } },
    { content: fmtMoney(data.totais.valor_total_servicos), styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] } },
    { content: fmtMoney(data.totais.valor_recebido), styles: { halign: 'right', fontStyle: 'bold', fillColor: [220, 252, 231], textColor: [22, 101, 52] } },
    { content: fmtMoney(data.totais.valor_despesas), styles: { halign: 'right', fontStyle: 'bold', fillColor: [254, 226, 226], textColor: [153, 27, 27] } },
  ]);

  autoTable(doc, {
    startY: cursorY,
    head: [['Cliente', 'Serviço', 'Responsável', 'Status', 'Início', 'Valor', 'Recebido', 'Despesas']],
    body: servicosBody,
    theme: 'grid',
    styles: {
      fontSize: 7.5,
      cellPadding: 2,
      lineColor: COR_BORDA,
      lineWidth: 0.1,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: COR_PRIMARIA,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'left',
    },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: 'bold' },
      1: { cellWidth: 50 },
      2: { cellWidth: 28 },
      3: { cellWidth: 26 },
      4: { cellWidth: 18 },
      5: { halign: 'right', cellWidth: 24, fontStyle: 'bold' },
      6: { halign: 'right', cellWidth: 24, textColor: [22, 101, 52] },
      7: { halign: 'right', cellWidth: 24, textColor: [153, 27, 27] },
    },
    margin: { left: margin, right: margin },
    didDrawPage: () => {
      // Rodapé em todas as páginas
      const p = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFontSize(7);
      doc.setTextColor(...COR_CLARA);
      doc.setFont('helvetica', 'normal');
      doc.text(
        `${nomeEmpresa} · Sistema AgroLink CRM`,
        margin,
        pageHeight - 5
      );
      doc.text(
        `Página ${p} de {total_pages_count_string}`,
        pageWidth - margin,
        pageHeight - 5,
        { align: 'right' }
      );
      doc.text(
        `Gerado em ${fmtDateTime(data.gerado_em)}`,
        pageWidth / 2,
        pageHeight - 5,
        { align: 'center' }
      );
    },
  });

  // =================================================================
  // 8) TABELA DE DESPESAS DETALHADAS (só se houver despesas)
  // =================================================================
  const despesasDet = data.despesas_detalhadas || [];
  if (despesasDet.length > 0) {
    // Nova página para deixar a seção limpa
    doc.addPage();

    // Título da seção
    doc.setFillColor(...COR_PRIMARIA);
    doc.rect(0, 0, pageWidth, 14, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`DESPESAS DETALHADAS (${despesasDet.length})`, margin, 9.5);

    doc.setTextColor(...COR_MEDIA);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Total: ${fmtMoney(data.totais.valor_despesas)}`,
      pageWidth - margin,
      9.5,
      { align: 'right', baseline: 'middle' }
    );
    doc.setTextColor(255, 255, 255);

    const despesasBody = despesasDet.map(d => [
      fmtDate(d.data),
      d.categoria_nome || 'Sem categoria',
      d.servico_titulo || '—',
      d.cliente_nome || '—',
      d.observacoes || d.descricao || '—',
      fmtMoney(d.valor),
    ]);

    despesasBody.push([
      { content: 'TOTAL', colSpan: 5, styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249], textColor: COR_ESCURA } },
      { content: fmtMoney(data.totais.valor_despesas), styles: { halign: 'right', fontStyle: 'bold', fillColor: [254, 226, 226], textColor: [153, 27, 27] } },
    ]);

    autoTable(doc, {
      startY: 20,
      head: [['Data', 'Tipo de Despesa', 'Serviço', 'Cliente', 'Observação', 'Valor']],
      body: despesasBody,
      theme: 'grid',
      styles: {
        fontSize: 7.5,
        cellPadding: 2,
        lineColor: COR_BORDA,
        lineWidth: 0.1,
        overflow: 'linebreak',
      },
      headStyles: {
        fillColor: COR_PRIMARIA,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'left',
      },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 40, fontStyle: 'bold' },
        2: { cellWidth: 55 },
        3: { cellWidth: 45 },
        4: { cellWidth: 'auto', textColor: COR_MEDIA },
        5: { halign: 'right', cellWidth: 26, fontStyle: 'bold', textColor: [153, 27, 27] },
      },
      margin: { left: margin, right: margin, top: 20 },
      didDrawPage: () => {
        const p = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(7);
        doc.setTextColor(...COR_CLARA);
        doc.setFont('helvetica', 'normal');
        doc.text(
          `${nomeEmpresa} · Sistema AgroLink CRM`,
          margin,
          pageHeight - 5
        );
        doc.text(
          `Página ${p} de {total_pages_count_string}`,
          pageWidth - margin,
          pageHeight - 5,
          { align: 'right' }
        );
        doc.text(
          `Gerado em ${fmtDateTime(data.gerado_em)}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: 'center' }
        );
      },
    });
  }

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages('{total_pages_count_string}');
  }

  // =================================================================
  // Salvar PDF
  // =================================================================
  const dataStr = new Date().toISOString().slice(0, 10);
  const nomeArquivo = `Relatorio_Financeiro_${nomeEmpresa.replace(/[^a-z0-9]/gi, '_')}_${dataStr}.pdf`;
  doc.save(nomeArquivo);
}
